import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Map, MapPin, Moon, Sun, Monitor, LogOut,
  Menu, X, UsersRound, MessageSquareWarning, Activity, Heart, Dog, FileText, CircleAlert, Shield, Megaphone, Database, Newspaper
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFilters } from '../context/FilterContext';
import NotificationDropdown from './NotificationDropdown';
import { DIVISION } from '../constants';
import { hasAdminManagementAccess, hasCampaignAccess, hasNewsAccess } from '../config/adminAccess';

// Helper function to get initial theme
const getInitialTheme = () => {
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme) {
    return savedTheme;
  }
  return 'system';
};

// Helper function to check if dark mode should be active
const shouldUseDarkMode = (theme) => {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const Layout = () => {
  const { admin, adminScope, logout } = useAuth();
  const { resetFilters } = useFilters();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const [theme, setTheme] = useState(getInitialTheme);
  const [darkMode, setDarkMode] = useState(() => shouldUseDarkMode(getInitialTheme()));
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle theme changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
    setDarkMode(shouldUseDarkMode(theme));

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemChange = (e) => {
      if (theme === 'system') {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleSystemChange);
    return () => mediaQuery.removeEventListener('change', handleSystemChange);
  }, [theme]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Reset filters when navigating to a different page
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      resetFilters();
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, resetFilters]);

  const cycleTheme = () => {
    setTheme(current => {
      if (current === 'system') return 'light';
      if (current === 'light') return 'dark';
      return 'system';
    });
  };

  const getThemeIcon = () => {
    if (theme === 'system') return <Monitor size={18} />;
    if (theme === 'light') return <Sun size={18} />;
    return <Moon size={18} />;
  };

  const getThemeLabel = () => {
    if (theme === 'system') return 'System';
    if (theme === 'light') return 'Light';
    return 'Dark';
  };

  // Check if user has admin management access
    const canAccessAdminManagement = hasAdminManagementAccess(admin?.email);
    const canAccessCampaigns = hasCampaignAccess(admin?.email);
    const canAccessNews = hasNewsAccess(admin?.email);

    const menuItems = [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
      { icon: MapPin, label: 'Live Map', path: '/live-map' },
      { icon: Map, label: 'Culled Dog View', path: '/culled-dog-view' },
      { icon: Dog, label: 'Registered Pet View', path: '/pet-registered-view' },
      { icon: Activity, label: 'Dog Bite Reports', path: '/dog-bite-reports' },
      { icon: MessageSquareWarning, label: 'Dog Complaints', path: '/complaints' },
      { icon: CircleAlert, label: 'Manhole Complaints', path: '/manhole-complaints' },
      { icon: FileText, label: 'Report Table', path: '/report-table' },
      { icon: Heart, label: 'Health Users', path: '/health-users' },
      { icon: UsersRound, label: 'UC/MC', path: '/users' },
      ...(canAccessNews ? [{ icon: Newspaper, label: 'News', path: '/news' }] : []),
      // Campaigns, Admin Management, UC Data Manager only visible to authorized emails (see config/adminAccess.js)
      ...(canAccessCampaigns ? [{ icon: Megaphone, label: 'Campaigns', path: '/campaigns' }] : []),
      ...(canAccessAdminManagement ? [{ icon: Shield, label: 'Admin Management', path: '/admin-management' }] : []),
      ...(canAccessAdminManagement ? [{ icon: Database, label: 'UC Data Manager', path: '/uc-data-manager' }] : [])
    ];

  return (
    <div className={`min-h-screen relative ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Gradient Mesh Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-32 -left-32 w-96 h-96 rounded-full blur-[100px] ${darkMode ? 'bg-blue-500/15' : 'bg-blue-400/25'}`} />
        <div className={`absolute top-1/3 -right-32 w-80 h-80 rounded-full blur-[100px] ${darkMode ? 'bg-purple-500/12' : 'bg-purple-400/20'}`} />
        <div className={`absolute -bottom-32 left-1/3 w-96 h-96 rounded-full blur-[100px] ${darkMode ? 'bg-emerald-500/12' : 'bg-emerald-400/20'}`} />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Header */}
      <div className={`fixed top-0 left-0 right-0 h-14 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} shadow-sm z-20 lg:hidden flex items-center justify-between px-4`}>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <Link to="/" className="absolute left-1/2 -translate-x-1/2">
          <img
            src={darkMode ? '/logo/logo_dark_mode.png' : '/logo/logo_light_mode.png'}
            alt="WeWatch Logo"
            className="h-8 rounded-lg object-contain"
          />
        </Link>
        <div className="w-10" /> {/* Spacer for balance */}
      </div>

      <div
        className={`fixed left-0 top-0 h-full overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg z-40 transition-all duration-300 ease-in-out
          hidden lg:flex lg:flex-col ${sidebarExpanded ? 'w-56' : 'w-16'}`}
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
      >
        <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} h-16 flex items-center shrink-0`}>
          <Link to="/" className="flex items-center gap-3 overflow-hidden cursor-pointer">
            <img
              src={darkMode ? '/logo/logo_dark_mode.png' : '/logo/logo_light_mode.png'}
              alt="WeWatch Logo"
              className={`h-10 min-w-[40px] rounded-lg object-contain transition-all duration-300 ${sidebarExpanded ? '' : 'h-8'}`}
            />
          </Link>
        </div>

        <nav className="p-2 flex-1 overflow-y-auto scrollbar-hide">
          {menuItems.map((item, index) => (
            <NavLink
              key={index}
              to={item.path}
              title={!sidebarExpanded ? item.label : ''}
              className={({ isActive }) =>
                `w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-all duration-200 ${isActive
                  ? darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600'
                  : darkMode ? 'hover:bg-blue-800/40 text-gray-300 hover:text-blue-300' : 'hover:bg-blue-100 text-gray-700 hover:text-blue-600'
                }`
              }
            >
              <item.icon size={20} className="min-w-[20px]" />
              <span className={`text-sm whitespace-nowrap transition-opacity duration-300 ${sidebarExpanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
                {item.label}
              </span>
            </NavLink>
          ))}
        </nav>

        <div className={`p-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} transition-all duration-300 shrink-0 ${sidebarExpanded ? 'opacity-100' : 'opacity-0'}`}>
          <p className={`text-[10px] text-center font-medium whitespace-nowrap ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Developed by <a href="https://wa.me/923007722777" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 transition-colors">Nexis Tech Solutions</a>
          </p>
        </div>
      </div>

      {/* Sidebar - Mobile */}
      <div
        className={`fixed left-0 top-0 h-full overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg z-40 transition-all duration-300 ease-in-out
          lg:hidden flex flex-col ${mobileMenuOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full'}`}
      >
        <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} h-14 flex items-center justify-between shrink-0`}>
          <div className="flex items-center gap-3">
            <img
              src={darkMode ? '/logo/logo_dark_mode.png' : '/logo/logo_light_mode.png'}
              alt="WeWatch Logo"
              className="h-8 rounded-lg object-contain"
            />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="p-2 flex-1 overflow-y-auto scrollbar-hide">
          {menuItems.map((item, index) => (
            <NavLink
              key={index}
              to={item.path}
              className={({ isActive }) =>
                `w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-all duration-200 ${isActive
                  ? darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600'
                  : darkMode ? 'hover:bg-blue-800/40 text-gray-300 hover:text-blue-300' : 'hover:bg-blue-100 text-gray-700 hover:text-blue-600'
                }`
              }
            >
              <item.icon size={20} className="min-w-[20px]" />
              <span className="text-sm whitespace-nowrap">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} shrink-0`}>
          <p className={`text-[10px] text-center font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Developed by <a href="https://wa.me/923007722777" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 transition-colors">Nexis Tech Solutions</a>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className={`pt-14 lg:pt-0 min-h-screen transition-all duration-300 ${sidebarExpanded ? 'lg:ml-56' : 'lg:ml-16'}`}>
        {/* Global Header */}
        <div className={`sticky top-0 z-10 h-16 ${darkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} border-b backdrop-blur-sm`}>
          <div className="px-4 sm:px-6 h-full flex items-center justify-between max-w-[1600px] mx-auto">
            <div>
              <h1 className={`text-lg sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                WeWatch
              </h1>
              <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {adminScope?.accessLevel === 'tehsil'
                  ? `${adminScope.tehsil} Tehsil, ${adminScope.district}`
                  : adminScope?.accessLevel === 'district'
                    ? `${adminScope.district} District`
                    : `${DIVISION} Division`
                } - Monitoring System
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Theme Toggle */}
              <button
                onClick={cycleTheme}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${darkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                title={`Current: ${getThemeLabel()} mode`}
              >
                {getThemeIcon()}
                <span className="text-xs hidden sm:inline">{getThemeLabel()}</span>
              </button>

              {/* Notifications */}
              <NotificationDropdown darkMode={darkMode} />

              {/* User Avatar & Logout */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {admin?.firstName?.[0]}{admin?.lastName?.[0]}
                  </div>
                  <div className="hidden md:block">
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {admin?.firstName} {admin?.lastName}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {admin?.role || 'Administrator'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={logout}
                  className={`p-2 rounded-lg transition-all ${darkMode ? 'hover:bg-red-900/30 text-gray-400 hover:text-red-400' : 'hover:bg-red-50 text-gray-500 hover:text-red-600'
                    }`}
                  title="Logout"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content - Outlet renders child routes */}
        <Outlet context={{ darkMode }} />
      </div>
    </div>
  );
};

export default Layout;
